import { useEffect, useState } from "react";
import { ClientFactory } from "@/main/common/ClientFactory";
import type { DiscussionPageDataResponse } from "@/generated-client";
import logger from "@/lib/logger";
import { countBodyWords, PASSAGE_ANCHOR_MIN_WORDS } from "@/main/react/discussion/passage/mapBlocksToDom";

export type UsePassageBlockIdsOptions = {
    /** The route key `/ajax/discussion/page-data/{slug}` accepts: the slug, else the public id. */
    discussionSlugOrPublicId?: string;
    /**
     * `passageBlockIds` as the page already has it: an array (possibly empty) when this load came through
     * `/page-data`, `undefined` when it came through the server-rendered dataset, which does not carry
     * the list (2026-09-15: `discussion.jte` emits no passage attribute; `DiscussionPage` fetches
     * `/page-data` only on an in-app soft navigation).
     */
    providedBlockIds?: ReadonlyArray<string | null> | null;
    /** The rendered body, once mounted; the word count that decides whether a fetch is worth it. */
    bodyRoot: HTMLElement | null;
    /** Re-keys the fetch decision; the body node is replaced when the HTML changes. */
    contentHtml?: string;
    enabled: boolean;
};

/**
 * The block-id list `mapBlocksToDom` joins against (D4), from whichever path this page load took.
 *
 * <p>On a soft navigation the list arrives with the page data and is returned as is. On a direct load
 * nothing carries it, so the list is fetched from `/page-data` after mount, and ONLY when the body is
 * long enough to be eligible by the mirrored constant (`PASSAGE_ANCHOR_MIN_WORDS`): ~18% of posts, so the
 * common case costs no request at all. The server remains the authority on eligibility; a post the
 * client thinks is long enough and the server does not simply answers with an empty list. A failed
 * fetch resolves to no anchors and logs, never to a fallback state (A5).
 */
export function usePassageBlockIds({
    discussionSlugOrPublicId,
    providedBlockIds,
    bodyRoot,
    contentHtml,
    enabled,
}: UsePassageBlockIdsOptions): ReadonlyArray<string | null> | null {
    const [fetched, setFetched] = useState<{ key: string; ids: ReadonlyArray<string | null> } | null>(null);

    const routeKey = (discussionSlugOrPublicId ?? "").trim();
    const mustFetch = enabled
        && providedBlockIds === undefined
        && routeKey.length > 0
        && bodyRoot !== null
        && countBodyWords(bodyRoot) >= PASSAGE_ANCHOR_MIN_WORDS;

    useEffect(() => {
        if (!mustFetch || fetched?.key === routeKey) {
            return;
        }
        let cancelled = false;
        void ClientFactory.getDiscussionPageData<DiscussionPageDataResponse>(routeKey)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const ids = Array.isArray(response.data?.passageBlockIds) ? response.data.passageBlockIds : [];
                setFetched({ key: routeKey, ids });
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                logger.warn("[usePassageBlockIds] page-data fetch failed; rendering no passage anchors", error);
                setFetched({ key: routeKey, ids: [] });
            });
        return () => {
            cancelled = true;
        };
        // `contentHtml` re-keys the decision on purpose: a body swap is a new post as far as the join is
        // concerned, and `mustFetch` already folds in the measured node.
    }, [contentHtml, fetched?.key, mustFetch, routeKey]);

    if (!enabled) {
        return null;
    }
    if (providedBlockIds !== undefined) {
        return providedBlockIds ?? null;
    }
    return fetched && fetched.key === routeKey ? fetched.ids : null;
}
